"use client";

import React, { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Image from "next/image";
import dayjs, { Dayjs } from "dayjs";
import { DatePickerQuestion as DatePickerQuestionType } from "@/commonTypes/question";
import { getAssetUrl } from "@/utils/assetUrl";

const MIN_AGE = 18;
export interface DatePickerQuestionProps {
  question: DatePickerQuestionType;
  value: string | null;
  onChange: (questionId: string, value: string | null) => void;
  disabled?: boolean;
  infoToast?: ReactNode;
}

export default function DatePickerQuestion({
  question,
  value,
  onChange,
  disabled = false,
  infoToast,
}: DatePickerQuestionProps) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = React.useState(false);

  const { questionId, questionTranslationKey, translationKey } = question;
  const questionText = t(questionTranslationKey);
  const placeholder = t(translationKey);

  const maxDate = dayjs().subtract(MIN_AGE, "year");
  const dateValue = value ? dayjs(value) : null;

  const handleChange = (newValue: Dayjs | null) => {
    onChange(questionId, newValue?.format("YYYY") ?? null);
  };

  return (
    <div className="question-container">
      <h1 className="question-title">{questionText}</h1>

      {infoToast && <div>{infoToast}</div>}

      <div className="date-picker-container">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label={placeholder}
            value={dateValue}
            onChange={handleChange}
            disabled={disabled}
            open={isOpen}
            maxDate={maxDate}
            views={["year"]}
            yearsOrder="desc"
            yearsPerRow={3}
            closeOnSelect={true}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            slots={{
              openPickerIcon: () => (
                <Image
                  src={getAssetUrl("/images/calendar_icon.svg")}
                  alt=""
                  width={24}
                  height={24}
                  unoptimized
                />
              ),
            }}
            slotProps={{
              actionBar: {
                actions: [],
              },
              textField: {
                id: `date-picker-${questionId}`,
                fullWidth: true,
                onClick: () => setIsOpen(true),
                onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
                inputProps: {
                  readOnly: true,
                },
              },
              popper: {
                disablePortal: true,
                placement: "bottom",
              },
              desktopPaper: {
                className: "select-menu-paper",
              },
            }}
          />
        </LocalizationProvider>
      </div>
    </div>
  );
}
